import React from 'react'
import classNames from 'classnames'

import type { Icon } from '@slite/icons'

import type { TooltipOnHoverProps } from '../TooltipOnHover/TooltipOnHover'
import { TooltipOnHover } from '../TooltipOnHover/TooltipOnHover'
import { useMenuContext } from './MenuProvider'

type BaseMenuItemContentProps = {
  icon?: Icon | React.ReactNode
  iconClassName?: string
  children: React.ReactNode
  isSelected?: boolean
  isDisabled?: boolean
  isChecked?: boolean
}

export const BaseMenuItemContent = ({
  icon,
  iconClassName,
  children,
  isDisabled,
  isSelected,
}: BaseMenuItemContentProps) => {
  const { size } = useMenuContext()

  const renderIcon = () => {
    if (!icon) return null

    const className = classNames(
      'margin-left-4 margin-right-8 flexbox-no-shrink',
      iconClassName,
      {
        'shade-30': isDisabled,
        'shade-20': !isDisabled && !isSelected,
      }
    )

    if (React.isValidElement(icon)) {
      return <span className={className}>{icon}</span>
    }

    const IconComponent = icon as Icon
    return (
      <IconComponent className={className} size={size === 'small' ? 12 : 16} />
    )
  }

  return (
    <>
      {renderIcon()}
      {children}
    </>
  )
}

export type BaseMenuItemProps = {
  alignItems?: 'center' | 'flex-start' | 'flex-end'
  children: (_className: string) => React.ReactNode
  tooltip?: TooltipOnHoverProps['content']
  tooltipPositions?: TooltipOnHoverProps['positions']
}

export const BaseMenuItem = ({
  alignItems = 'center',
  tooltip,
  tooltipPositions,
  children: renderChildren,
}: BaseMenuItemProps) => {
  const _className = classNames(
    'width-full border-none no-outline',
    `flexbox flexbox-align-${alignItems}`,
    'shade-05  background-shade-50-on-focus transition radius-large text-align-left'
  )
  return tooltip ? (
    <TooltipOnHover
      content={tooltip}
      positions={tooltipPositions ?? ['right', 'left', 'top']}
      target={({ ref, props }) => (
        <div ref={ref} {...props} className="padding-horizontal-4">
          {renderChildren(_className)}
        </div>
      )}
    />
  ) : (
    <div className="padding-horizontal-4">{renderChildren(_className)}</div>
  )
}
